import {
  Alert,
  Button,
  Input,
  Typography
} from 'antd';
import { useState } from 'react';
import { KeyOutlined, MailOutlined } from '@ant-design/icons';
import CodeInput from '../../UI/input/CodeInput';
import useCountdown from '../../hooks/useCountdown';
import {
  formatRecoveryCode,
  loginFallback,
  loginRecovery,
  messageFor,
  normaliseRecoveryCode,
  sendLoginFallbackCode,
  statusOf,
  TOO_MANY_REQUESTS_HOURLY,
  UNAVAILABLE,
  type LoginResponse
} from '../../../service/mfa.service';

/**
 * The two ways in when the authenticator app is out of reach.
 *
 * Both still require the **password**: what they replace is the second factor, not the first.
 * And both **remove the authenticator**, because Keycloak refuses to issue tokens without a
 * valid TOTP code and the backend cannot produce one: it does not hold the secret. So getting
 * in without the app means the app stops being the factor, and the user re-enrols afterwards.
 * That is not a footnote. It is said on the chooser and again on each route, because a user
 * who did not expect it finds their second factor quietly gone.
 *
 * Which one to reach for is not a matter of taste. Every account **is** an email address, so
 * the emailed code works for anyone whether or not they enrolled the email factor; a recovery
 * code is for the day the mailbox itself is gone. The chooser says that rather than presenting
 * two equal buttons.
 *
 * The password arrives as a prop and stays in memory for the life of this screen: step 2 of the
 * emailed route re-sends it with the code, because nothing is parked server-side between the
 * two calls.
 */

interface LostAuthenticatorProps {
  email: string;
  password: string;
  /** A standard login body, with `reenrol_totp_required: true`. */
  onSignedIn: (data: LoginResponse) => void | Promise<void>;
  onBack: () => void;
  /**
   * The account behind this sign-in is gone. Nothing here can recover from that: the address
   * is a prop rather than a field, so there is no typo to correct and nothing to retry. The
   * host takes the user back to the sign-in screen with this as the reason.
   */
  onAccountGone: (reason: string) => void;
}

type Route = 'choose' | 'email' | 'recovery';

const RESEND_COOLDOWN_SECONDS = 60;

const REMOVAL_WARNING =
  'Signing in this way removes the authenticator app from your account. You will be asked to '
  + 'set it up again afterwards.';

const LostAuthenticator = ({
  email,
  password,
  onSignedIn,
  onBack,
  onAccountGone
}: LostAuthenticatorProps) => {
  const [route, setRoute] = useState<Route>('choose');
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * A 503 from the send, and whether it was a resend, so the retry repeats the same call. Kept
   * apart from `error` because it is the one failure here that is neither final nor anybody's
   * fault, and it must not read like the 404 sitting beside it in the same handler.
   */
  const [unavailable, setUnavailable] = useState<{
    message: string;
    isResend: boolean;
  } | null>(null);

  const resendCooldown = useCountdown(0);

  const goBackToChooser = () => {
    setRoute('choose');
    setCode('');
    setRecoveryCode('');
    setError(null);
    setNotice(null);
    setUnavailable(null);
    resendCooldown.stop();
  };

  /**
   * Step 1 of the emailed route. It cannot verify the password first, because the grant that
   * would do it is the one TOTP is blocking, so this used to answer a constant 202 to any
   * address and the screen advanced on anything that was not an exception. It no longer does.
   *
   * **A 404 here is not the same event it is on the recovery screen.** There, somebody typed
   * an address and got it wrong. Here the address is a prop, carried from a sign-in the server
   * had already answered with a second-factor challenge, so it is one the directory recognised
   * moments ago. There is no typo to fix and no retry that would help: the account is gone, and
   * the only honest move is to say so and go back to the start.
   *
   * A 503 is the opposite: the lookup failed and says nothing about the account, so it gets a
   * retry rather than a verdict.
   */
  const requestEmailedCode = async (isResend = false) => {
    setIsSubmitting(true);
    setError(null);
    setUnavailable(null);
    try {
      await sendLoginFallbackCode(email);
      setRoute('email');
      setCode('');
      setNotice(
        isResend
          ? 'A new code is on its way.'
          : 'A code has been sent to the address on your account.'
      );
      resendCooldown.restart(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      const status = statusOf(err);
      if (status === 404) {
        onAccountGone(
          'That account no longer exists. Sign in with a different address, or ask an '
          + 'administrator.'
        );
        return;
      }
      if (status === 503) {
        setUnavailable({ 
          message: messageFor(err, {}, UNAVAILABLE), 
          isResend 
        });
        return;
      }
      setError(
        messageFor(err, {
          429: TOO_MANY_REQUESTS_HOURLY
        }, 'Could not send a code right now. Please try again shortly.')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Step 2. Deliberately **not** wired to `CodeInput`'s `onComplete`: a right code with a
   * wrong password still clears the authenticator, so the last keystroke of the code must not
   * be what commits to that. The explicit button under the warning is the confirmation.
   */
  const submitEmailedCode = async () => {
    setIsSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const { data } = await loginFallback(email, password, code);
      await onSignedIn(data);
    } catch (err: any) {
      setCode('');
      setError(
        messageFor(err, {
          400: 'That code is not right, or it has expired.',
          401: 'That code is not right, or it has expired.',
          429: 'Too many attempts. Please wait a minute and try again.'
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitRecoveryCode = async () => {
    setIsSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const { data } = await loginRecovery(
        email,
        password,
        normaliseRecoveryCode(recoveryCode)
      );
      await onSignedIn(data);
    } catch (err: any) {
      // One 401 covers a wrong code, an unknown account and a code already spent. That is
      // deliberate on the server's side. It must not reveal which, so this cannot be more
      // specific than the server was willing to be.
      if (statusOf(err) === 401) {
        setError('That code is not valid. Check it and try again.');
        return;
      }
      setError(
        messageFor(err, {
          429: 'Too many attempts. Please wait a minute and try again.'
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const recoveryIsComplete = normaliseRecoveryCode(recoveryCode).length === 8;

  return (
    <div>
      {error && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message={error}
          closable
          onClose={() => setError(null)}
        />
      )}
      {unavailable && (
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="We could not send a code just now"
          description={
            <div className="text-[13px]">
              <div className="mb-3">
                {unavailable.message} This is a fault on our side, not a verdict on your
                account: no code has gone out, and the account is very likely still there.
              </div>
              <Button
                size="small"
                loading={isSubmitting}
                onClick={() => requestEmailedCode(unavailable.isResend)}
              >
                Try again
              </Button>
            </div>
          }
        />
      )}
      {notice && (
        <Alert
          type="success"
          showIcon
          className="mb-4"
          message={notice}
          closable
          onClose={() => setNotice(null)}
        />
      )}

      {route === 'choose' && (
        <>
          <Typography.Paragraph type="secondary" className="text-[13px]">
            Two ways in without your authenticator app.
          </Typography.Paragraph>

          <Alert
            type="warning"
            showIcon
            className="mb-4"
            message={<span className="text-[13px]">{REMOVAL_WARNING}</span>}
          />

          {/* Icons sit in their own flex column rather than going through antd's `icon`
              prop, which drops them into the inline flow and wraps the second line back
              under the icon. */}
          <Button
            block
            size="large"
            className="mb-3 !h-auto !py-3 !text-left"
            loading={isSubmitting}
            onClick={() => requestEmailedCode()}
          >
            <span className="flex items-start gap-3 w-full">
              <MailOutlined className="mt-[3px] shrink-0" />
              <span className="whitespace-normal">
                <span className="block font-semibold">Email me a code</span>
                <span className="block text-[12px] opacity-70 font-normal">
                  Sent to your account&apos;s address. Use this if you can still read your
                  email.
                </span>
              </span>
            </span>
          </Button>

          <Button
            block
            size="large"
            className="!h-auto !py-3 !text-left"
            disabled={isSubmitting}
            onClick={() => {
              setRoute('recovery');
              setError(null);
              setNotice(null);
            }}
          >
            <span className="flex items-start gap-3 w-full">
              <KeyOutlined className="mt-[3px] shrink-0" />
              <span className="whitespace-normal">
                <span 
                  className="block font-semibold"
                >Use a recovery code</span>
                <span className="block text-[12px] opacity-70 font-normal">
                  One of the ten you saved. Use this if your mailbox is gone too.
                </span>
              </span>
            </span>
          </Button>

          <div className="mt-4">
            <Button type="link" className="!px-0" onClick={onBack}>
              Back to the code from my app
            </Button>
          </div>
        </>
      )}

      {route === 'email' && (
        <>
          <Typography.Paragraph type="secondary" className="text-[13px]">
            Enter the six-digit code we sent to your account&apos;s email address.
          </Typography.Paragraph>

          <div className="flex justify-center my-6">
            <CodeInput
              value={code}
              onChange={setCode}
              disabled={isSubmitting}
              autoFocus
              status={error ? 'error' : undefined}
            />
          </div>

          <Alert
            type="warning"
            showIcon
            className="mb-4"
            message={<span className="text-[13px]">{REMOVAL_WARNING}</span>}
          />

          <Button
            type="primary"
            size="large"
            block
            danger
            loading={isSubmitting}
            disabled={code.length !== 6}
            onClick={submitEmailedCode}
          >
            Verify and remove authenticator
          </Button>

          <div className="flex items-center justify-between gap-2 mt-4">
            <Button type="link" className="!px-0" onClick={goBackToChooser}>
              Back
            </Button>
            <Button
              onClick={() => requestEmailedCode(true)}
              disabled={resendCooldown.isRunning || isSubmitting}
            >
              {resendCooldown.isRunning
                ? `Resend in ${resendCooldown.secondsLeft}s`
                : 'Send a new code'}
            </Button>
          </div>
        </>
      )}

      {route === 'recovery' && (
        <>
          <Typography.Paragraph type="secondary" className="text-[13px]">
            Enter one of the recovery codes you saved when you set up two-factor
            authentication. Each one works once.
          </Typography.Paragraph>

          <Input
            size="large"
            autoFocus
            className="my-4 text-center tracking-[0.3em] font-mono"
            placeholder="XXXX-XXXX"
            maxLength={9}
            value={recoveryCode}
            status={error ? 'error' : undefined}
            disabled={isSubmitting}
            // Case and the hyphen are cosmetic: the backend normalises either way, so
            // whatever the user types is reshaped as they go rather than rejected.
            onChange={(event) => setRecoveryCode(formatRecoveryCode(event.target.value))}
            onPressEnter={() => {
              if (recoveryIsComplete && !isSubmitting) submitRecoveryCode();
            }}
          />

          <Alert
            type="warning"
            showIcon
            className="mb-4"
            message={<span className="text-[13px]">{REMOVAL_WARNING}</span>}
          />

          <Button
            type="primary"
            size="large"
            block
            danger
            loading={isSubmitting}
            disabled={!recoveryIsComplete}
            onClick={submitRecoveryCode}
          >
            Verify and remove authenticator
          </Button>

          <div className="mt-4">
            <Button type="link" className="!px-0" onClick={goBackToChooser}>
              Back
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default LostAuthenticator;
